/* eslint-disable no-irregular-whitespace */
import { css } from '@emotion/react';
import { PageProps } from 'gatsby';
import { ContactSection } from '@/components/general/ContactSection';
import { Layout } from '@/components/general/Layout';
import { NewsPageTitle } from '@/components/general/NewsPageTitle';
import { Seo } from '@/components/general/Seo';
import { dayjs } from '@/lib/dayjs';
import { getScreen } from '@/styles/media-query';
import { News } from '@/types/cms-types';

type TemplateProps = {
  pageContext: {
    node: News;
  };
} & PageProps;

const Template: React.FC<TemplateProps> = ({
  pageContext: { node },
  location,
}) => {
  return (
    <Layout location={location}>
      <Seo
        title={node.title}
        description="【Uniforce】IPO準備クラウドのお知らせ。コラムなどの記事情報を記載しています。"
      />
      <article>
        <NewsPageTitle
          css={css`
            margin-bottom: 40px;

            @media ${getScreen('pc')} {
              margin-bottom: 64px;
            }
          `}
          title={node.title}
          coverImage={node.coverImage}
        />
        <div
          css={css`
            max-width: 1000px;
            margin: 0 auto;
            padding: 0 30px 80px;

            @media ${getScreen('pc')} {
              padding: 0 30px 120px;
            }
          `}
        >
          <p
            css={css`
              font: normal normal normal 14px/18px 'din-2014', sans-serif;
              letter-spacing: 0.55px;
              color: #7d7d88;
            `}
          >
            <time dateTime={dayjs(node.publishedAt).toISOString()}>
              {dayjs(node.publishedAt).format('YYYY/MM/DD')}
            </time>
          </p>
          <h1
            css={css`
              font: normal normal bold 22px/36px 'Noto Sans JP', sans-serif;
              letter-spacing: 1.32px;
              color: #00b3a7;
              margin: 8px 0 16px;

              @media ${getScreen('pc')} {
                font: normal normal bold 30px/44px 'Noto Sans JP', sans-serif;
                letter-spacing: 2.07px;
                margin: 16px 0 16px;
              }
            `}
          >
            {node.title}
          </h1>
          <p
            css={css`
              background: #00b3a7;
              text-align: center;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0px;
              color: #ffffff;
              border-radius: 44px;
              color: #fff;
              padding: 6px 16px;
              width: max-content;
              margin-bottom: 32px;
            `}
          >
            {node.category.title}
          </p>
          <div
            css={css`
              font: normal normal 500 14px/24px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.56px;
              color: #5a5b6a;

              h2 {
                font: normal normal bold 22px/36px 'Noto Sans JP', sans-serif;
                letter-spacing: 1.32px;
                color: #00b3a7;
                margin: 32px 0 16px;

                @media ${getScreen('pc')} {
                  font: normal normal bold 30px/44px 'Noto Sans JP', sans-serif;
                  letter-spacing: 2.07px;
                  margin: 64px 0 24px;
                }
              }

              strong {
                font-weight: bold;
              }

              img {
                width: 100%;
                height: auto;
              }
            `}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: node.body,
              }}
            />
            {(node.overview || []).map((o, i) => (
              <div key={i}>
                <h2
                  css={css`
                    gap: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin: 64px 0 24px;

                    @media ${getScreen('pc')} {
                      gap: 15px;
                      margin: 72px 0 24px;
                    }

                    &::after {
                      content: '';
                      display: block;
                      width: 48px;
                      height: 1px;
                      background: #00b3a7;
                    }
                  `}
                >
                  <span
                    css={css`
                      text-align: center;
                      font: normal normal bold 22px/32px 'Noto Sans JP',
                        sans-serif;
                      letter-spacing: 1.52px;
                      color: #00b3a7;

                      @media ${getScreen('pc')} {
                        font: normal normal bold 26px/37px 'Noto Sans JP',
                          sans-serif;
                        letter-spacing: 1.79px;
                      }
                    `}
                  >
                    {o.head}
                  </span>
                </h2>
                <dl
                  css={css`
                    border: 1px solid #d1d8dd;
                    border-radius: 8px;
                    padding: 4px;

                    @media ${getScreen('pc')} {
                      padding: 32px;
                    }
                  `}
                >
                  {o.list.map((item, i) => (
                    <div
                      key={i}
                      css={css`
                        padding: 16px 12px;

                        &:not(:first-of-type) {
                          border-top: 1px solid #ebeff1;
                        }

                        @media ${getScreen('pc')} {
                          display: grid;
                          padding: 12px;
                          grid-template-columns: 160px 1fr;
                        }
                      `}
                    >
                      <dt
                        css={css`
                          text-align: left;
                          font: normal normal 500 14px/24px 'Noto Sans JP',
                            sans-serif;
                          letter-spacing: 0.28px;
                          color: #5a5b6a;
                        `}
                      >
                        {item.term}
                      </dt>
                      <dd
                        css={css`
                          text-align: left;
                          font: normal normal normal 14px/24px 'Noto Sans JP',
                            sans-serif;
                          letter-spacing: 0.43px;
                          color: #5a5b6a;
                          margin-top: 4px;
                          white-space: pre-line;

                          @media ${getScreen('pc')} {
                            margin-top: 0;
                          }
                        `}
                      >
                        {item.definition}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </article>
      <ContactSection
        title1="Uniforce IPO準備クラウドで"
        title2="「IPOの準備を効率的」"
        title3="に"
        title4="進めませんか？"
        text="IPO準備クラウドの機能詳細や無料デモのご相談はこちら"
      />
    </Layout>
  );
};

export default Template;
